import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Lightbulb, Trophy } from "lucide-react";

const ABOUT_IMAGE = "https://customer-assets.emergentagent.com/job_learn-code-kids-3/artifacts/749qoa82_ChatGPT%20Image%2011%20%D0%BC%D0%B0%D1%80.%202026%20%D0%B3.%2C%2000_45_27.png";

const values = [
  {
    icon: Lightbulb,
    title: "Project-Based Learning",
    desc: "Kids create games, apps, and robots from day one of learning.",
    color: "#F28C38",
    bg: "#FFF1E6",
  },
  {
    icon: Users,
    title: "Small Groups",
    desc: "Maximum 8 students per class for personalized attention.",
    color: "#1677FF",
    bg: "#EAF2FF",
  },
  {
    icon: Trophy,
    title: "Confidence Through Building",
    desc: "Every child showcases their work and celebrates growth.",
    color: "#00A676",
    bg: "#E7FFF6",
  },
];

export const AboutPreview = () => {
  return (
    <section data-testid="about-preview-section" className="pt-[35px] pb-[35px] md:py-24 bg-white">
      <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Image with blob shape */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-[#EAF2FF] rounded-[2rem] rotate-2"></div>
              <img
                src={ABOUT_IMAGE}
                alt="Child learning to code in a classroom"
                className="relative rounded-2xl w-full h-[380px] object-cover shadow-lg"
                loading="lazy"
              />
              {/* Small accent card */}
              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl px-4 py-3 shadow-lg border border-border">
                <p className="font-accent text-2xl tracking-wider text-[#D52B1E]">ONLINE</p>
                <p className="text-[10px] text-muted-foreground font-semibold">Learn Anywhere</p>
              </div>
            </div>
          </motion.div>

          {/* Right: Text */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold bg-[#FFE9E7] text-[#D52B1E] mb-4">
              About Us
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-5">
              Where Kids Become{" "}
              <span className="text-[#1677FF]">Creators</span>
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed mb-8">
              Maple Code Academy is an online coding school dedicated to helping children aged 6–14 discover the joy of technology. Our hands-on programs in coding, robotics, and digital creativity empower kids to think critically, solve problems, and build amazing projects.
            </p>
            <Link
              to="/about"
              className="text-sm font-semibold text-[#1677FF] hover:underline"
            >
              Learn more about us &rarr;
            </Link>
          </motion.div>
        </div>

        {/* Value Chips */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-16">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="flex items-start gap-4 p-5 pr-[30px] rounded-2xl bg-white border border-border card-hover"
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: v.bg }}
              >
                <v.icon className="w-6 h-6" style={{ color: v.color }} />
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-1">{v.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{v.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutPreview;
